import React, { createContext, useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { useGeneral } from '../../Context/GeneralContext';

const TelegramContext = createContext();

export function TelegramProvider(props) {
    const { tokenR } = useGeneral();
    const [telegrams, setTelegrams] = useState([]);
    const [areasAtencion, setAreasAtencion] = useState([]);
    const [chats, setChats] = useState([]);
    const [isOptionsDisabled, setIsOptionsDisabled] = useState(true);
    const [selectionModel, setSelectionModel] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const getHeaders = () => ({ authorization: `Bearer ${tokenR.current}`, Accept: 'application/json' });

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const closeSnackbar = useCallback(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    const handleApiError = useCallback(async (res, fallback) => {
        try {
            const body = await res.json();
            showSnackbar(body?.message || body?.title || fallback, 'error');
        } catch {
            showSnackbar(fallback, 'error');
        }
    }, [showSnackbar]);

    const getTelegrams = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/TelegramConfig/GetGrupos`, {
                headers: getHeaders(),
            });
            if (!res.ok) return;
            const obj = await res.json();
            obj.forEach(item => { item.id = item.codigo; });
            setTelegrams(obj);
        } catch (err) { console.error(err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR]);

    const getChats = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/TelegramConfig/GetChats`, {
                headers: getHeaders(),
            });
            if (!res.ok) return;
            setChats(await res.json());
        } catch (err) { console.error(err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR]);

    const getAreasAtencion = useCallback(async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController?categoria=AreasAtencion`,
                { headers: getHeaders() }
            );
            if (!res.ok) return;
            const obj = await res.json();
            obj.forEach(item => { item.id = item.codigo; });
            setAreasAtencion(obj);
        } catch (err) { console.error(err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR]);

    // Retorna true si fue exitoso, false si hubo error (los modales deciden si cerrar)
    const postTelegram = useCallback(async (data) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/TelegramConfig/NuevoGrupo`, {
                method: 'POST',
                body: JSON.stringify(data),
                headers: { ...getHeaders(), 'Content-Type': 'application/json' },
            });
            if (!res.ok) { await handleApiError(res, 'Error al crear el grupo'); return false; }
            showSnackbar('Grupo creado correctamente');
            setSelectionModel([]);
            setIsOptionsDisabled(true);
            getTelegrams();
            return true;
        } catch (err) {
            showSnackbar('Error de conexión al crear el grupo', 'error');
            return false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR, getTelegrams, handleApiError, showSnackbar]);

    const putTelegram = useCallback(async (data) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/TelegramConfig/EditarGrupo`, {
                method: 'PUT',
                body: JSON.stringify(data),
                headers: { ...getHeaders(), 'Content-Type': 'application/json' },
            });
            if (!res.ok) { await handleApiError(res, 'Error al editar el grupo'); return false; }
            showSnackbar('Grupo actualizado correctamente');
            getTelegrams();
            return true;
        } catch (err) {
            showSnackbar('Error de conexión al editar el grupo', 'error');
            return false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR, getTelegrams, handleApiError, showSnackbar]);

    const delTelegram = useCallback(async (codigo) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/TelegramConfig/BorrarGrupo`, {
                method: 'DELETE',
                body: JSON.stringify({ codigo }),
                headers: { ...getHeaders(), 'Content-Type': 'application/json' },
            });
            if (!res.ok) { await handleApiError(res, 'Error al eliminar el grupo'); return false; }
            showSnackbar('Grupo eliminado correctamente');
            setSelectionModel([]);
            setIsOptionsDisabled(true);
            getTelegrams();
            return true;
        } catch (err) {
            showSnackbar('Error de conexión al eliminar el grupo', 'error');
            return false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR, getTelegrams, handleApiError, showSnackbar]);

    useEffect(() => {
        getTelegrams();
        getAreasAtencion();
        getChats();
    }, [getTelegrams, getAreasAtencion, getChats]);

    const TelegramValues = useMemo(() => ({
        telegrams,
        areasAtencion, chats,
        isOptionsDisabled, setIsOptionsDisabled,
        selectionModel, setSelectionModel,
        snackbar, closeSnackbar,
        postTelegram, putTelegram, delTelegram,
    }), [telegrams, areasAtencion, chats, isOptionsDisabled,
        selectionModel, snackbar, closeSnackbar,
        postTelegram, putTelegram, delTelegram]);

    return <TelegramContext.Provider value={TelegramValues} {...props} />;
}

export function useTelegram() {
    const context = useContext(TelegramContext);
    if (!context) throw new Error('useTelegram debe estar dentro del proveedor TelegramContext');
    return context;
}
